import { getTopMarqueeBanner } from '@/lib/cms/getTopMarqueeBanner'
import useGlobalStore from '@/store/useGlobalStore'
import { useEffect, useState, useRef } from 'react'
import { useNfrCheck } from '@/lib/hooks/services/useNfrCheck'

export const useGetTopMarqueeBanner = () => {
  const {
    app: { locale },
  } = useGlobalStore()

  const [isLoading, setIsLoading] = useState(false)
  const [topMarqueeText, setTopMarqueeText] = useState<string | null>(null)
  const [topMarqueeBackgroundColor, setTopMarqueeBackgroundColor] = useState<
    string | null
  >(null)
  const { isNfrUser } = useNfrCheck()
  const isNfrUserRef = useRef(isNfrUser)
  const abortControllerRef = useRef<AbortController | null>(null)

  useEffect(() => {
    // Update ref with latest value
    isNfrUserRef.current = isNfrUser

    // Cancel any previous API call
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Create new abort controller for this call
    abortControllerRef.current = new AbortController()
    ;(async () => {
      setIsLoading(true)

      try {
        // Use the ref value to ensure we always have the latest isNfrUser value
        const topMarqueeData = await getTopMarqueeBanner({
          locale,
          isNfrUser: isNfrUserRef.current,
          signal: abortControllerRef.current?.signal,
        })

        // Check if this call was aborted
        if (abortControllerRef.current?.signal.aborted) {
          return
        }

        if (!topMarqueeData) {
          setIsLoading(false)
          setTopMarqueeText(null)
          setTopMarqueeBackgroundColor(null)
          return
        }

        if ('error' in topMarqueeData) {
          setIsLoading(false)
          setTopMarqueeText(null)
          setTopMarqueeBackgroundColor(null)
          return
        }

        setTopMarqueeText(
          topMarqueeData.data.topMarqueeBanner[0].data.text || null,
        )

        // Cosmic doesn't allow us to set the background color, so we have to hardcode it here
        setTopMarqueeBackgroundColor('#173962')
        setIsLoading(false)
      } catch (error) {
        // Only handle errors if the call wasn't aborted
        if (!abortControllerRef.current?.signal.aborted) {
          console.error('Error fetching top marquee banner:', error)
          setIsLoading(false)
          setTopMarqueeText(null)
          setTopMarqueeBackgroundColor(null)
        }
      }
    })()
  }, [locale, isNfrUser])

  // Cleanup function to abort any pending API calls
  useEffect(
    () => () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    },
    [],
  )

  return { isLoading, topMarqueeText, topMarqueeBackgroundColor }
}
